/**
 * Small identity marks: a brand's initials on a tile of its own colours, and the logo of the
 * platform a campaign runs on (X or YouTube). Both are decorative: the brand's or the platform's
 * name is always written next to them.
 */

/** Pairs of colours for brands' tiles. A brand always gets the same pair. */
const BRAND_TONES = [
  "from-emerald-300 to-teal-500",
  "from-sky-300 to-indigo-500",
  "from-violet-300 to-fuchsia-500",
  "from-amber-200 to-orange-500",
  "from-rose-300 to-pink-500",
  "from-cyan-300 to-blue-500",
] as const;

/** The first letters of a brand's first two words: "Northwind Coffee" is "NC". */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return words
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("");
}

/** A brand's initials on a tile, coloured from its name. */
export function BrandMark({ name, size = "md" }: { name: string; size?: "md" | "lg" }) {
  let hash = 0;
  for (const character of name) {
    hash = (hash * 31 + (character.codePointAt(0) ?? 0)) >>> 0;
  }
  const tone = BRAND_TONES[hash % BRAND_TONES.length];
  return (
    <span
      aria-hidden
      className={`grid shrink-0 place-items-center bg-gradient-to-br font-display font-semibold text-ink-950 shadow-[inset_0_1px_0_rgb(255_255_255/0.45)] ${tone} ${
        size === "lg" ? "size-12 rounded-2xl text-base" : "size-9 rounded-xl text-xs"
      }`}
    >
      {initials(name) || "?"}
    </span>
  );
}

/** The logo of the platform a campaign runs on. */
export function PlatformMark({
  platform,
  className = "size-3.5",
}: {
  platform: "X" | "YOUTUBE";
  className?: string;
}) {
  return platform === "X" ? (
    <svg aria-hidden viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  ) : (
    <svg aria-hidden viewBox="0 0 24 24" className={className}>
      <path
        fill="#ff0033"
        d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z"
      />
      <path fill="#fff" d="M9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  );
}
